/**
 * Anonymous session management for temporary documents
 * Handles session ID generation, storage, and validation
 */

const ANONYMOUS_SESSION_KEY = "scriba_anonymous_session";
const ANONYMOUS_NAME_KEY = "scriba_anonymous_name";

export interface AnonymousSession {
  sessionId: string;
  userName: string;
  createdAt: number;
}

/**
 * Generates a unique anonymous session ID
 */
export function generateAnonymousSessionId(): string {
  const timestamp = Date.now();
  const randomPart = Math.random().toString(36).substring(2, 14);
  return `anon_${timestamp}_${randomPart}`;
}

/**
 * Generates a fun random name for anonymous users
 */
export function generateAnonymousName(): string {
  const adjectives = [
    "Silly",
    "Goofy",
    "Wacky",
    "Zany",
    "Bizarre",
    "Quirky",
    "Nutty",
    "Daffy",
    "Kooky",
    "Loopy",
    "McFunny",
  ];
  const nouns = [
    "Goofball",
    "Jokester",
    "Sillypants",
    "Wackypants",
    "Gigglemonster",
    "Quirkmaster",
    "McNutty",
    "von Bizarre",
    "the Great",
    "McZany",
  ];

  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];

  return `${adjective} ${noun}`;
}

/**
 * Gets or creates an anonymous session
 * Uses localStorage for client-side persistence
 */
export function getOrCreateAnonymousSession(): AnonymousSession {
  if (typeof window === "undefined") {
    // Server-side: generate new session
    return {
      sessionId: generateAnonymousSessionId(),
      userName: generateAnonymousName(),
      createdAt: Date.now(),
    };
  }

  // Client-side: check localStorage
  try {
    const stored = localStorage.getItem(ANONYMOUS_SESSION_KEY);
    const storedName = localStorage.getItem(ANONYMOUS_NAME_KEY);

    if (stored && storedName) {
      return {
        sessionId: stored,
        userName: storedName,
        createdAt: Date.now(), // We don't persist creation time
      };
    }
  } catch (error) {
    console.warn("Failed to read from localStorage:", error);
  }

  // Create new session
  const sessionId = generateAnonymousSessionId();
  const userName = generateAnonymousName();

  try {
    localStorage.setItem(ANONYMOUS_SESSION_KEY, sessionId);
    localStorage.setItem(ANONYMOUS_NAME_KEY, userName);
  } catch (error) {
    console.warn("Failed to write to localStorage:", error);
  }

  return {
    sessionId,
    userName,
    createdAt: Date.now(),
  };
}

/**
 * Gets the current anonymous session without creating a new one
 */
export function getAnonymousSession(): AnonymousSession | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const sessionId = localStorage.getItem(ANONYMOUS_SESSION_KEY);
    const userName = localStorage.getItem(ANONYMOUS_NAME_KEY);

    if (sessionId && userName) {
      return {
        sessionId,
        userName,
        createdAt: Date.now(),
      };
    }
  } catch (error) {
    console.warn("Failed to read from localStorage:", error);
  }

  return null;
}

/**
 * Clears the anonymous session from storage
 */
export function clearAnonymousSession(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(ANONYMOUS_SESSION_KEY);
    localStorage.removeItem(ANONYMOUS_NAME_KEY);
  } catch (error) {
    console.warn("Failed to clear localStorage:", error);
  }
}

/**
 * Validates an anonymous session ID format
 */
export function isValidAnonymousSessionId(sessionId: string): boolean {
  return /^anon_\d+_[a-z0-9]+$/.test(sessionId);
}
